import {
  type DisplayMarkVenue,
  displayCurrentValue,
  displayMarkedOutcomeAmount,
  displayVenue,
} from './display-mark.js';
import {
  available,
  isI64,
  isNonNegativeI64,
  isSignedMagnitudeU128,
  isU64,
  unavailable,
} from './integer.js';
import type { OutcomePoolQuoteContext } from './outcome-pool.js';
import { exitFeesForMarkedAmount, type PnlBreakdownInput } from './pnl-breakdown.js';
import type { MarketPoolQuoteSnapshot } from './snapshot-context.js';
import { positionQuoteContext } from './trader-pnl.js';
import type { PoolMathResult } from './types.js';

export interface ConsolidatedPnlHolding extends Omit<PnlBreakdownInput, 'context'> {
  snapshot: MarketPoolQuoteSnapshot;
  market: string;
  outcomeIndex: number;
  outcomeMint: string;
}

/**
 * Account-level P&L across wallets. Holdings of the same outcome pool are
 * exited together: the second wallet's tokens sell into the curve the first
 * already moved, so the total is what all wallets would actually receive,
 * which is at most the sum of the per-position values.
 */
export interface ConsolidatedPnlBreakdown {
  poolCount: number;
  realizedGrossD6: bigint;
  realizedNetD6: bigint;
  realizedPenaltyD6: bigint;
  costBasisGrossD6: bigint;
  costBasisNetD6: bigint;
  currentValueGrossD6: bigint;
  exitFeesD6: bigint;
  exitPenaltyD6: bigint;
  currentValueNetD6: bigint;
  prizeClaimableD6: bigint;
  unrealizedGrossD6: bigint;
  unrealizedNetD6: bigint;
  totalGrossD6: bigint;
  totalNetD6: bigint;
}

interface PoolAccumulator {
  snapshot: MarketPoolQuoteSnapshot;
  context: OutcomePoolQuoteContext;
  venue: DisplayMarkVenue;
  markedOutcomeAmountD6: bigint;
}

export function buildConsolidatedPnlBreakdown(
  holdings: readonly ConsolidatedPnlHolding[],
): PoolMathResult<ConsolidatedPnlBreakdown> {
  const pools = new Map<string, PoolAccumulator>();
  let realizedGrossD6 = 0n;
  let realizedNetD6 = 0n;
  let realizedPenaltyD6 = 0n;
  let costBasisGrossD6 = 0n;
  let costBasisNetD6 = 0n;
  let prizeClaimableD6 = 0n;

  for (const holding of holdings) {
    if (holding.claimRightsBalanceD6 < 0n) return unavailable('claim_rights_replay_deficit');
    if (
      !isU64(holding.outcomeBalanceD6) ||
      !isU64(holding.claimRightsBalanceD6) ||
      !isU64(holding.prizeClaimableD6) ||
      !isNonNegativeI64(holding.remainingCostBasisGrossD6) ||
      !isNonNegativeI64(holding.remainingCostBasisNetD6) ||
      !isI64(holding.realizedPnlGrossD6) ||
      !isI64(holding.realizedPnlNetD6) ||
      !isU64(holding.realizedPenaltyD6)
    ) {
      return unavailable('invalid_amount');
    }
    const context = positionQuoteContext(
      holding.snapshot,
      holding.market,
      holding.outcomeIndex,
      holding.outcomeMint,
    );
    if (!context.available) return context;
    const venue = displayVenue(context.value);
    if (!venue.available) return venue;
    const marked = displayMarkedOutcomeAmount(
      context.value,
      holding.outcomeBalanceD6,
      holding.claimRightsBalanceD6,
      venue.value,
    );
    if (!marked.available) return marked;

    // One outcome mint is one pool on both venues (its curve, then its AMM).
    const poolKey = `${holding.market}:${holding.outcomeMint}`;
    const pool = pools.get(poolKey);
    if (pool === undefined) {
      pools.set(poolKey, {
        snapshot: holding.snapshot,
        context: context.value,
        venue: venue.value,
        markedOutcomeAmountD6: marked.value,
      });
    } else {
      if (pool.snapshot !== holding.snapshot) return unavailable('mixed_pool_snapshot');
      pool.markedOutcomeAmountD6 += marked.value;
      if (!isU64(pool.markedOutcomeAmountD6)) return unavailable('invalid_amount');
    }
    realizedGrossD6 += holding.realizedPnlGrossD6;
    realizedNetD6 += holding.realizedPnlNetD6;
    realizedPenaltyD6 += holding.realizedPenaltyD6;
    costBasisGrossD6 += holding.remainingCostBasisGrossD6;
    costBasisNetD6 += holding.remainingCostBasisNetD6;
    prizeClaimableD6 += holding.prizeClaimableD6;
  }

  let currentValueGrossD6 = 0n;
  let exitFeesD6 = 0n;
  let exitPenaltyD6 = 0n;
  for (const pool of pools.values()) {
    const gross = displayCurrentValue(pool.context, pool.venue, pool.markedOutcomeAmountD6);
    if (!gross.available) return gross;
    const fees = exitFeesForMarkedAmount(
      pool.context,
      pool.venue,
      pool.markedOutcomeAmountD6,
      gross.value,
    );
    if (!fees.available) return fees;
    currentValueGrossD6 += gross.value;
    exitFeesD6 += fees.value.feesD6;
    exitPenaltyD6 += fees.value.penaltyD6;
  }
  const currentValueNetD6 = currentValueGrossD6 - exitFeesD6 - exitPenaltyD6;
  const unrealizedGrossD6 = currentValueGrossD6 + prizeClaimableD6 - costBasisGrossD6;
  const unrealizedNetD6 = currentValueNetD6 + prizeClaimableD6 - costBasisNetD6;
  if (
    !isSignedMagnitudeU128(realizedGrossD6) ||
    !isSignedMagnitudeU128(realizedNetD6) ||
    !isSignedMagnitudeU128(unrealizedGrossD6) ||
    !isSignedMagnitudeU128(unrealizedNetD6) ||
    !isSignedMagnitudeU128(realizedGrossD6 + unrealizedGrossD6) ||
    !isSignedMagnitudeU128(realizedNetD6 + unrealizedNetD6)
  ) {
    return unavailable('invalid_amount');
  }
  return available({
    poolCount: pools.size,
    realizedGrossD6,
    realizedNetD6,
    realizedPenaltyD6,
    costBasisGrossD6,
    costBasisNetD6,
    currentValueGrossD6,
    exitFeesD6,
    exitPenaltyD6,
    currentValueNetD6,
    prizeClaimableD6,
    unrealizedGrossD6,
    unrealizedNetD6,
    totalGrossD6: realizedGrossD6 + unrealizedGrossD6,
    totalNetD6: realizedNetD6 + unrealizedNetD6,
  });
}
